import React, { useState, useEffect } from "react";
import { Form, Input, Button, Table, message, Card, Space, Typography, Modal, Tooltip, Tag } from "antd";
import { 
  PlusCircleOutlined, // 更换图标
  DeleteOutlined, 
  SearchOutlined, 
  ClearOutlined, 
  ReloadOutlined,
  ExperimentOutlined, // 用于批量查询
  WarningOutlined // 用于重试
} from "@ant-design/icons";
import { analyzeSingleAddress, retryFailedAddresses } from "../api";

const { Text } = Typography;
const STORAGE_KEY = "bsc_address_list_tech"; // 修改 storage key 以免与旧版冲突

// 工具函数：从localStorage读取数据
const getStoredAddresses = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to load addresses:', error);
    return [];
  }
};

// 工具函数：保存数据到localStorage
const saveAddresses = (addresses) => {
  try {
    if (addresses && addresses.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(addresses));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    return true;
  } catch (error) {
    console.error('Failed to save addresses:', error);
    message.error('地址保存失败，请检查浏览器存储权限');
    return false;
  }
};

export default function AddressForm({ onQuery, failedAddresses, setFailedAddresses, noTxAddresses, loading }) {
  const [form] = Form.useForm();
  const [addresses, setAddresses] = useState(getStoredAddresses);
  const [queryingAddress, setQueryingAddress] = useState("");

  useEffect(() => {
    saveAddresses(addresses);
  }, [addresses]);

  const addAddress = (values) => {
    if (!/^0x[a-fA-F0-9]{40}$/.test(values.address)) {
      message.error("请输入有效的BSC地址 (0x开头，40位十六进制字符)");
      return;
    }
    if (addresses.some(addr => addr.address.toLowerCase() === values.address.toLowerCase())) {
      message.warning("该地址已存在于列表中");
      return;
    }
    const newAddress = { ...values, key: values.address }; // 添加 key 属性
    const newAddresses = [...addresses, newAddress];
    setAddresses(newAddresses);
    form.resetFields();
    message.success('地址已添加');
  };

  const removeAddress = (addressToRemove) => {
    const newAddresses = addresses.filter(item => item.address !== addressToRemove);
    setAddresses(newAddresses);
    message.success('地址已移除');
  };

  const clearAllAddresses = () => {
    Modal.confirm({
      title: '确认清空',
      content: '确定要清空所有已添加的地址吗？此操作不可撤销。',
      okText: '确认清空',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        setAddresses([]);
        message.success('已清空所有地址');
      }
    });
  };

  const handleSingleQuery = async (address, remark) => {
    if (loading) return;
    setQueryingAddress(address);
    try {
      await onQuery([{ address, remark, key: address }]);
    } finally {
      setQueryingAddress("");
    }
  };

  const handleBatchQuery = async () => {
    if (loading || addresses.length === 0) {
      if(addresses.length === 0) message.info("请先添加地址再进行批量查询");
      return;
    }
    await onQuery(addresses);
  };

  const handleRetry = async () => {
    if (!failedAddresses || failedAddresses.length === 0 || loading) {
      if(!failedAddresses || failedAddresses.length === 0) message.info("当前没有查询失败的地址");
      return;
    }
    try {
      const response = await retryFailedAddresses(failedAddresses.map(f => ({ address: f.address, remark: f.remark })));
      await onQuery(addresses);
      const newFailures = response.results ? response.results.filter(r => !r.success) : [];
      setFailedAddresses(newFailures);

      if (newFailures.length === 0) {
        message.success({ content: "所有失败的地址已重试成功！", key: 'retry' });
      } else {
        message.warning({ content: `${newFailures.length}个地址仍然失败`, key: 'retry' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      message.error({ content: `重试失败: ${errorMsg}`, key: 'retry' });
    }
  };

  return (
    <Card 
      title="地址输入与管理"
      bordered={false}
      style={{ marginBottom: 24 }}
      headStyle={{ borderBottom: '1px solid rgba(0, 168, 232, 0.3)'}}
    >
      <Form form={form} layout="inline" onFinish={addAddress} style={{ marginBottom: 20 }}>
        <Form.Item 
          name="address" 
          rules={[{ required: true, message: "请输入BSC地址" }, { pattern: /^0x[a-fA-F0-9]{40}$/, message: "请输入有效的BSC地址" }]}
          style={{ flexGrow: 1, minWidth: 300 }}
        >
          <Input 
            prefix={<Text type="secondary" style={{ marginRight: 4 }}>0x</Text>}
            placeholder="输入BSC地址 (例如: 0x...)" 
          />
        </Form.Item>
        <Form.Item name="remark" style={{ minWidth: 150 }}>
          <Input placeholder="备注 (可选)" />
        </Form.Item>
        <Form.Item>
          <Tooltip title="添加新地址到列表">
            <Button 
              type="primary" 
              icon={<PlusCircleOutlined />} 
              htmlType="submit"
              ghost // 幽灵按钮在暗色主题下可能效果更好
            >
              添加地址
            </Button>
          </Tooltip>
        </Form.Item>
      </Form>

      {addresses.length > 0 && (
        <>
          <Table
            dataSource={addresses}
            columns={[
              { 
                title: "BSC 地址", 
                dataIndex: "address",
                ellipsis: true,
                width: '35%',
                render: addr => (
                  <Space>
                    <a 
                      href={`https://bscscan.com/address/${addr}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#00A8E8' }}
                    >
                      {addr}
                    </a>
                    {noTxAddresses.some(item => item.address === addr) && (
                      <Tag color="warning">今日未交易</Tag>
                    )}
                  </Space>
                )
              },
              { title: "备注", dataIndex: "remark", width: '35%', ellipsis: true },
              {
                title: "操作",
                key: "action",
                width: '30%',
                render: (_, record) => (
                  <Space size="small">
                    <Tooltip title={`查询地址: ${record.address.substring(0,10)}...`}>
                      <Button
                        type="dashed"
                        icon={<SearchOutlined />}
                        onClick={() => handleSingleQuery(record.address, record.remark)}
                        size="small"
                        loading={loading && queryingAddress === record.address}
                        disabled={loading && queryingAddress !== record.address}
                      >
                        单独查询
                      </Button>
                    </Tooltip>
                    <Tooltip title={`从列表中移除: ${record.address.substring(0,10)}...`}>
                      <Button 
                        danger 
                        icon={<DeleteOutlined />}
                        onClick={() => removeAddress(record.address)}
                        size="small"
                        disabled={loading}
                        ghost
                      >
                        移除
                      </Button>
                    </Tooltip>
                  </Space>
                )
              }
            ]}
            rowKey="address"
            pagination={{ 
              pageSize: 5, 
              size: "small",
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}个地址`
            }}
            size="middle"
            style={{ marginBottom: 20 }}
            scroll={{ x: true }}
          />

          <Space wrap style={{ justifyContent: 'center', width: '100%' }}>
            <Tooltip title="对列表中的所有地址进行批量分析">
              <Button
                type="primary"
                icon={<ExperimentOutlined />}
                onClick={handleBatchQuery}
                size="large"
                loading={loading && !queryingAddress}
                disabled={loading || addresses.length === 0}
                style={{minWidth: 150}}
              >
                {loading && !queryingAddress ? '分析中...' : `批量分析 (${addresses.length})`}
              </Button>
            </Tooltip>
            
            {failedAddresses && failedAddresses.length > 0 && (
              <Tooltip title="尝试重新分析之前失败的地址">
                <Button
                  type="primary"
                  danger
                  icon={<WarningOutlined />}
                  onClick={handleRetry}
                  size="large"
                  loading={loading}
                  disabled={loading}
                  style={{minWidth: 180}}
                  ghost
                >
                  重试失败 ({failedAddresses.length})
                </Button>
              </Tooltip>
            )}

            {noTxAddresses && noTxAddresses.length > 0 && (
              <Button
                type="dashed"
                icon={<SearchOutlined />}
                size="large"
                style={{minWidth: 180}}
                onClick={() => handleBatchQuery(noTxAddresses)}
              >
                重新检查未交易 ({noTxAddresses.length})
              </Button>
            )}

            {addresses.length > 0 && (
              <Tooltip title="清空当前列表中的所有地址">
                <Button 
                  icon={<ClearOutlined />} 
                  onClick={clearAllAddresses}
                  size="large"
                  disabled={loading}
                  style={{minWidth: 150}}
                >
                  清空列表
                </Button>
              </Tooltip>
            )}
          </Space>
        </>
      )}
    </Card>
  );
}
